import hiredCoachLogo from '@/assets/hiredcoach-logo.svg';
import PremiumLockModal from '@/components/subscription/PremiumLockModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { InterviewAccessResponse, InterviewResponse, JobResponse, useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import { BookOpenCheck, Check, ChevronLeft, ChevronRight, Loader2, Lock, Play, Swords, Timer } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface InterviewSectionProps {
  jobId?: string;
  interviews: InterviewResponse[];
  availableJobs?: JobResponse[];
  onInterviewCreated?: () => void;
  onInterviewsUpdate?: (interviews: InterviewResponse[]) => void;
  demoMode?: boolean;
  creationMode?: 'job' | 'select';
  emptyMessage?: string;
  listControls?: ReactNode;
}

const DEFAULT_FOCUS_AREAS = [
  'Core responsibilities',
  'Behavioral fit',
  'Problem solving',
  'Stakeholder communication',
];

const GENERAL_CATEGORY_OPTIONS = [
  {
    key: 'general_behavioral',
    label: 'General behavioral',
    description: 'HR-style behavior questions',
  },
  {
    key: 'experience_summarization',
    label: 'Experience overview',
    description: 'We\'ll generally ask about your background',
  },
  {
    key: 'tough_questions',
    label: 'Tough questions',
    description: 'Fair but challenging pressure checks',
  },
  {
    key: 'conflict_focus',
    label: 'Conflict focus',
    description: 'Conflicts or disagreements (and their resolutions) within the workplace',
  },
  {
    key: 'borderline_mean',
    label: 'Borderline mean questions',
    description: 'Jerks exist - might as well be prepared for them',
  }
] as const;

const buildInitialGeneralCategories = (): Record<string, boolean> =>
  Object.fromEntries(
    GENERAL_CATEGORY_OPTIONS.map((option) => [option.key, false])
  );

const GENERAL_JOB_VALUE = '__general__';

// Helper function to determine interview type based on question count
const getInterviewType = (interview: InterviewResponse): string => {
  // Try questions array first
  const questionCount = interview.questions?.length || 0;
  // Fallback to question_order array
  const orderCount = interview.question_order?.length || 0;
  
  // Use whichever count is available
  const count = questionCount > 0 ? questionCount : orderCount;
  
  if (count === 3) return 'Warmup';
  if (count === 5 || count === 7) return 'Study Session'; // Handle both 5 and 7 for Study Session
  if (count === 15) return 'Gauntlet';
  
  // If we still don't have a valid count, return a default but don't assume Gauntlet
  // This prevents the bug where refreshing interviews show as Gauntlet
  return count > 10 ? 'Gauntlet' : count > 5 ? 'Study Session' : 'Warmup';
};

// Helper to determine interview type from a raw count (used on create)
const getInterviewTypeFromCount = (count: number): string => {
  if (count === 3) return 'Warmup';
  if (count === 5 || count === 7) return 'Study Session';
  if (count === 15) return 'Gauntlet';
  return count > 10 ? 'Gauntlet' : count > 5 ? 'Study Session' : 'Warmup';
};

// Helper function to get interview type color (matching creation button colors)
const getInterviewTypeColor = (type: string): string => {
  switch (type) {
    case 'Warmup': return 'text-white bg-gradient-to-r from-green-500 to-green-600 border-green-500';
    case 'Study Session': return 'text-white bg-gradient-to-r from-blue-500 to-blue-600 border-blue-500';
    case 'Gauntlet': return 'text-white bg-gradient-to-r from-red-500 to-red-600 border-red-500';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

// Helper function to get score label (same as InterviewFeedback)
const getScoreLabel = (score: number) => {
  if (score > 3.9) return "S"
  else if (score > 3.4) return "A"
  else if (score > 2.9) return "B"
  else if (score > 1.9) return "C"
  else if (score > 1.4) return "D"
  else return "F"
};

// Helper function to get grade background color for the whole card
const getGradeCardBackground = (grade: number) => {
  const label = getScoreLabel(grade);
  const gradeColors = {
    S: 'bg-gradient-to-br from-amber-400 via-yellow-400 to-amber-500 border-amber-300',
    A: 'bg-green-300 border-green-400',
    B: 'bg-green-200 border-green-300',
    C: 'bg-yellow-200 border-yellow-300',
    D: 'bg-red-200 border-red-300',
    F: 'bg-red-500 border-red-600',
  };
  return gradeColors[label];
};

// Helper function to get grade text color
const getGradeTextColor = (grade: number) => {
  const label = getScoreLabel(grade);
  const textColors = {
    S: 'text-amber-900',
    A: 'text-green-900',
    B: 'text-green-800',
    C: 'text-yellow-800',
    D: 'text-red-800',
    F: 'text-red-100',
  };
  return textColors[label];
};

// Helper function to get status pill styling (clean, no backgrounds)
const getStatusPillStyle = (status: string) => {
  switch (status.toUpperCase()) {
    case 'CREATING':
      return 'text-yellow-600 border-yellow-400';
    case 'RESEARCHING':
      return 'text-blue-600 border-blue-400';
    case 'GENERATING':
      return 'text-purple-600 border-purple-400';
    case 'GRADING':
      return 'text-indigo-600 border-indigo-400';
    case 'FAILED':
      return 'text-red-700 border-red-300';
    default:
      return 'text-gray-600 border-gray-400';
  }
};

export default function InterviewSection({
  jobId,
  interviews,
  availableJobs = [],
  onInterviewCreated,
  onInterviewsUpdate,
  demoMode,
  creationMode = 'job',
  emptyMessage = 'No practice interviews yet. Create your first one above!',
  listControls,
}: InterviewSectionProps) {
  const { api } = useApiClient();
  const navigate = useNavigate();
  const [loadingType, setLoadingType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  // Premium lock modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeatureLabel, setPremiumFeatureLabel] = useState<string | undefined>(undefined);
  const [jobFocusAreas, setJobFocusAreas] = useState<string[]>([]);
  const [selectedInterviewCount, setSelectedInterviewCount] = useState<number | null>(null);
  const [selectedFocusArea, setSelectedFocusArea] = useState<string>('none');
  const [customFocusInstructions, setCustomFocusInstructions] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string>(jobId || GENERAL_JOB_VALUE);
  const [generalCategories, setGeneralCategories] = useState<Record<string, boolean>>(
    () => buildInitialGeneralCategories()
  );
  const [accessStatus, setAccessStatus] = useState<InterviewAccessResponse | null>(null);
  const [accessLoading, setAccessLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const isPremium = (user?.subscription_mode ?? 'free') === 'premium';
  const isDemo = !!demoMode;
  const isJobSelectorMode = creationMode === 'select';

  // Rotating fun facts for long-running generation/research states
  const FUN_FACTS: string[] = [
    'Octopuses have eight hearts and one very busy calendar app.',
    'Lightning is sky spaghetti deciding on a noodle path.',
    'Time zones were invented so sunsets wouldn\'t overlap.',
    'Mars is red because it blushed during a telescope compliment.',
    'Rain is sky confetti for plants doing a good job.',
    "Cows have best friends, and they get stressed when separated. Moo-d swings are real.",
    "A group of flamingos is called a flamboyance, and frankly they own it.",
    "Sloths can hold their breath longer than dolphins, mostly out of spite.",
    "Rats giggle when tickled, though it ruins their street cred.",
    "Sea cucumbers eject their insides as self-defense. Mood.",
    "Clouds can weigh millions of pounds, yet still look fluffy. Fitness goals.",
    "Crows remember faces—and grudges. Choose your bird enemies wisely.",
  ];
  const [funFactIndex, setFunFactIndex] = useState<number>(
    Math.floor(Math.random() * Math.max(1, FUN_FACTS.length))
  );

  const ITEMS_PER_PAGE = 6;

  // Sort interviews by creation date (newest first) and paginate
  const sortedInterviews = useMemo(() => {
    return [...interviews].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [interviews]);

  const totalPages = Math.ceil(sortedInterviews.length / ITEMS_PER_PAGE);
  const paginatedInterviews = sortedInterviews.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );
  const selectedJob = useMemo(
    () => availableJobs.find((job) => job.id === selectedJobId) ?? null,
    [availableJobs, selectedJobId]
  );
  const visibleFocusAreas = jobFocusAreas.length > 0 ? jobFocusAreas : DEFAULT_FOCUS_AREAS;

  // Check if any interviews are being generated or graded
  const hasGeneratingInterviews = interviews.some(interview => {
    const status = interview.status.toUpperCase();
    return status === 'CREATING' || status === 'RESEARCHING' || status === 'GENERATING';
  });

  // Check if any interviews are actively in fun-fact mode (RESEARCHING/GENERATING)
  const hasActiveFunFactMode = interviews.some(interview => {
    const status = interview.status.toUpperCase();
    return status === 'RESEARCHING' || status === 'GENERATING';
  });

  // Check if any interviews are incomplete (not finished by user)
  const hasIncompleteInterviews = interviews.some(interview => {
    const status = interview.status.toUpperCase();
    return status === 'INCOMPLETE';
  });

  const existingSelectedInterview = useMemo(() => {
    return interviews.find((interview) => {
      const status = interview.status.toUpperCase();
      if (status === 'COMPLETE' || status === 'FAILED' || status === 'GRADING') {
        return false;
      }

      if (isJobSelectorMode) {
        if (selectedJobId === GENERAL_JOB_VALUE) {
          return !interview.job_id;
        }
        return interview.job_id === selectedJobId;
      }

      if (jobId) {
        return interview.job_id === jobId;
      }

      return false;
    }) ?? null;
  }, [interviews, isJobSelectorMode, jobId, selectedJobId]);

  const selectedTargetHasExistingInterview = !!existingSelectedInterview;

  // Users cannot create interviews while one is being generated or left unfinished
  const canCreateInterviews = isDemo
    ? true
    : isJobSelectorMode
      ? !accessStatus?.creation_locked
      : !hasGeneratingInterviews && !hasIncompleteInterviews && !accessStatus?.creation_locked;

  const getQuestionSetAccess = (questionCount: number) =>
    accessStatus?.question_sets.find((item) => item.questions === questionCount) ?? null;

  const isCreationQuotaLocked =
    !isDemo && !!accessStatus?.creation_locked;

  const getQuotaBannerMessage = () => {
    if (!accessStatus) return null;
    return accessStatus.message;
  };

  const getExistingInterviewMessage = () => {
    if (!existingSelectedInterview) return null;
    if (isJobSelectorMode) {
      if (selectedJobId === GENERAL_JOB_VALUE) {
        return 'You already have an unfinished general interview. Finish that one before starting another.';
      }
      return 'You already have an unfinished interview for this job. Finish that one before starting another.';
    }
    return 'You already have an unfinished interview for this job. Finish that one before starting another.';
  };

  const getQuestionAccessDescription = (questionCount: number) => {
    const access = getQuestionSetAccess(questionCount);
    if (!access) {
      return `${questionCount} questions`;
    }
    if (access.allowed) {
      return `${questionCount} questions`;
    }
    if (access.reason === 'premium') {
      return 'Premium required';
    }
    if (access.reason === 'quota') {
      return accessStatus?.plan === 'free'
        ? 'Free plan weekly limit reached'
        : 'Limit reached for now';
    }
    return access.message;
  };

  // Function to refresh interviews smoothly without full page refresh
  const refreshInterviews = async () => {
    try {
      const updatedInterviews = isJobSelectorMode
        ? await api.getInterviews(true)
        : await api.getInterviewsByJob(jobId!);
      if (onInterviewsUpdate) {
        onInterviewsUpdate(updatedInterviews);
      }
    } catch (error) {
      console.error('Failed to refresh interviews:', error);
      // Fallback to full refresh if smooth update fails
      if (onInterviewCreated) {
        onInterviewCreated();
      }
    }
  };

  useEffect(() => {
    if (isDemo) return;

    if (isJobSelectorMode) {
      const focusAreas = (selectedJob?.interview_focus_areas || []).filter(Boolean);
      setJobFocusAreas(focusAreas.length > 0 ? focusAreas : DEFAULT_FOCUS_AREAS);
      return;
    }
    if (!jobId) {
      setJobFocusAreas(DEFAULT_FOCUS_AREAS);
      return;
    }

    const loadJob = async () => {
      try {
        const job = await api.getJobById(jobId);
        const focusAreas = (job.interview_focus_areas || []).filter(Boolean);
        setJobFocusAreas(focusAreas.length > 0 ? focusAreas : DEFAULT_FOCUS_AREAS);
      } catch (error) {
        console.error('Failed to load job interview focus areas:', error);
        setJobFocusAreas(DEFAULT_FOCUS_AREAS);
      }
    };

    loadJob();
  }, [api, isDemo, isJobSelectorMode, jobId, selectedJob]);

  // Set up polling for interview generation status
  useEffect(() => {
    if (isDemo || !hasGeneratingInterviews) {
      return;
    }

    const pollInterval = setInterval(() => {
      // Use smooth refresh instead of full page refresh
      refreshInterviews();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [hasGeneratingInterviews, jobId, api, onInterviewsUpdate, onInterviewCreated, isDemo]);

  // Initialize a random fun fact when RESEARCHING/GENERATING starts
  useEffect(() => {
    if (!hasActiveFunFactMode) return;
    setFunFactIndex(Math.floor(Math.random() * Math.max(1, FUN_FACTS.length)));
  }, [hasActiveFunFactMode]);

  // Rotate fun facts every 30s only while RESEARCHING/GENERATING
  useEffect(() => {
    if (!hasActiveFunFactMode) return;
    const id = setInterval(() => {
      setFunFactIndex((prev) => (prev + 1) % Math.max(1, FUN_FACTS.length));
    }, 30000);
    return () => clearInterval(id);
  }, [hasActiveFunFactMode]);

  useEffect(() => {
    if (jobId && !isJobSelectorMode) {
      setSelectedJobId(jobId);
    }
  }, [isJobSelectorMode, jobId]);

  useEffect(() => {
    if (isDemo) return;

    const loadAccess = async () => {
      try {
        setAccessLoading(true);
        const status = await api.getInterviewAccess();
        setAccessStatus(status);
      } catch (error) {
        console.error('Failed to load interview access status:', error);
      } finally {
        setAccessLoading(false);
      }
    };

    loadAccess();
  }, [api, isDemo]);

  useEffect(() => {
    if (isCreationQuotaLocked) {
      setSelectedInterviewCount(null);
    }
  }, [isCreationQuotaLocked]);

  // Handle creating interviews with different question counts
  const handleCreateInterview = async (questionCount: number) => {
    const type = getInterviewTypeFromCount(questionCount);
    setLoadingType(type);

    if (isDemo) {
      // In demo mode, do nothing silently
      setLoadingType(null);
      return;
    }

    const questionAccess = getQuestionSetAccess(questionCount);
    if (questionAccess && !questionAccess.allowed) {
      if (questionAccess.reason === 'premium') {
        setPremiumFeatureLabel(type);
        setShowPremiumModal(true);
      } else {
        toast.error(questionAccess.message);
      }
      setLoadingType(null);
      return;
    }

    if (selectedTargetHasExistingInterview) {
      toast.info(getExistingInterviewMessage() ?? 'You already have an unfinished interview for this target.');
      setLoadingType(null);
      return;
    }

    try {
      const chosenGeneralCategories = Object.entries(generalCategories)
        .filter(([, enabled]) => enabled)
        .map(([category]) => category);

      const result = await api.generateInterview({
        job_id: selectedJobId === GENERAL_JOB_VALUE ? undefined : selectedJobId,
        questions: questionCount,
        focus_area: selectedFocusArea === 'none' ? '' : selectedFocusArea,
        focus_instructions: customFocusInstructions.trim(),
        general_question_categories: chosenGeneralCategories,
        general_question_count: chosenGeneralCategories.length,
      });

      if (result.status === 'existing') {
        toast.info(result.message);
        await refreshInterviews();
        return;
      } else {
        toast.success(`${type} interview created successfully!`);
      }
      setSelectedInterviewCount(null);
      setSelectedFocusArea('none');
      setCustomFocusInstructions('');
      setGeneralCategories(buildInitialGeneralCategories());

      if (isJobSelectorMode) {
        navigate(`/interview/${result.interview_id}`);
        return;
      }

      // Use smooth refresh instead of full page refresh
      await refreshInterviews();
      const status = await api.getInterviewAccess();
      setAccessStatus(status);
    } catch (error: any) {
      const detailError =
        typeof error?.detail?.error === 'string' ? error.detail.error : '';

      if (
        error?.statusCode === 405 &&
        (
          detailError.includes('premium required')
          || detailError.includes('question set not allowed')
        )
      ) {
        setShowPremiumModal(true);
      } else {
        toast.error(formatInterviewAccessMessage(error, type));
      }
    } finally {
      setLoadingType(null);
    }
  };

  const handleInterviewClick = (interviewId: string) => {
    if (isDemo) {
      // Demo mode: no navigation or toast
      return;
    }
    navigate(`/interview/${interviewId}`);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Helper function to format status text
  const formatStatusText = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() + '...';
  };

  // Handle clicks on premium-locked actions
  const handleLockedClick = (label: string) => {
    if (isDemo) {
      // Demo mode: no modal or toast
      return;
    }
    setPremiumFeatureLabel(label);
    setShowPremiumModal(true);
  };

  const toggleGeneralCategory = (category: string) => {
    setGeneralCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const formatInterviewAccessMessage = (error: any, type: string) => {
    const detail = error?.detail;
    const detailError =
      typeof detail?.error === 'string' ? detail.error : '';

    if (detailError.includes('limit reached')) {
      const limit = detail?.limit;
      const window = detail?.window;
      const windowEnd = detail?.window_end
        ? new Date(detail.window_end).toLocaleDateString()
        : null;

      if (limit && windowEnd) {
        return `You have reached your ${window ?? 'current'} practice interview limit on the free plan (${limit}). You can create another interview after ${windowEnd}, or upgrade to Premium for more practice.`;
      }
      if (limit) {
        return `You have reached your current free-plan practice interview limit (${limit}).`;
      }
      return 'You have reached your current free-plan practice interview limit.';
    }

    if (
      detailError.includes('not allowed')
      || detailError.includes('premium required')
      || detailError.includes('question set not allowed')
    ) {
      return `${type} interviews are not available on your current plan.`;
    }

    return getUserErrorMessage(
      error,
      `Unable to create ${type} interview right now. Please try again later.`
    );
  };

  const getInterviewJobLabel = (interview: InterviewResponse) => {
    if (!interview.job_id) {
      return 'General interview';
    }
    const job = availableJobs.find((item) => item.id === interview.job_id);
    if (!job) {
      return 'Job interview';
    }
    return `${job.title} at ${job.company}`;
  };

  const selectInterviewType = (questionCount: number, premiumLabel?: string) => {
    if (isCreationQuotaLocked) {
      toast.error(getQuotaBannerMessage() ?? 'You have reached your current practice interview limit.');
      return;
    }
    const questionAccess = getQuestionSetAccess(questionCount);
    if (!isDemo && questionAccess && !questionAccess.allowed) {
      if (questionAccess.reason === 'premium') {
        handleLockedClick(premiumLabel ?? getInterviewTypeFromCount(questionCount));
      } else {
        toast.error(questionAccess.message);
      }
      return;
    }
    if (!isDemo && premiumLabel && !isPremium) {
      handleLockedClick(premiumLabel);
      return;
    }
    setSelectedInterviewCount((prev) => prev === questionCount ? null : questionCount);
  };

  const hasLockedCustomization = !isPremium && !isDemo;

  return (
    <Card className="mt-0 sm:mt-8 border-0 sm:border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-none sm:shadow-md hover:shadow-lg transition-shadow duration-300 rounded-none sm:rounded-lg mx-0">
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-center gap-3 sm:gap-6">
          <img src={hiredCoachLogo} alt="HiredCoach" className="h-10 w-10 sm:h-16 sm:w-16 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-blue-900 leading-tight">
              Personalized Practice Interviews
            </h2>
            {!hasGeneratingInterviews && !hasIncompleteInterviews && !isJobSelectorMode && (
              <p className="text-blue-700 text-sm sm:text-base mt-1 hidden sm:block">
                What do you have time for? The Coach is ready to help!
              </p>
            )}
            {isJobSelectorMode && (
              <p className="text-blue-700 text-sm sm:text-base mt-1">
                Pick an active job or keep it general. Unfinished interviews stay here until you wrap them up.
              </p>
            )}
            {hasGeneratingInterviews && (
              <p className="text-blue-700 text-sm sm:text-base mt-1">
                Generating an interview for you right now...
              </p>
            )}
            {!isDemo && accessStatus && !hasGeneratingInterviews && (
              <p className="text-blue-700 text-sm sm:text-base mt-1">
                {accessStatus.message}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        <div className="space-y-4 sm:space-y-6">
          {!isDemo && isCreationQuotaLocked && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950">
              <div className="font-semibold">Practice interview limit reached</div>
              <div className="mt-1">{getQuotaBannerMessage()}</div>
              {accessStatus?.plan === 'free' && (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-amber-800">
                    Your current membership does not include more interview creation this week.
                  </div>
                  <Button variant="outline" onClick={() => handleLockedClick('Premium practice interviews')}>
                    <Lock className="mr-2 h-4 w-4" />
                    View Premium
                  </Button>
                </div>
              )}
            </div>
          )}

          {!isDemo && !isCreationQuotaLocked && accessStatus?.plan === 'free' && (
            <div className="rounded-xl border border-blue-200 bg-white/80 px-4 py-3 text-sm text-blue-900">
              {accessStatus.message}
            </div>
          )}

          {/* Create Interview Buttons */}
          {(isDemo || isJobSelectorMode || (!hasGeneratingInterviews && !hasIncompleteInterviews)) && (
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                {/* Warmup Button */}
                <button
                  onClick={() => selectInterviewType(3)}
                  disabled={!canCreateInterviews || loadingType !== null || accessLoading || !!(getQuestionSetAccess(3) && !getQuestionSetAccess(3)?.allowed)}
                  className={`group relative rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                    selectedInterviewCount === 3
                      ? 'bg-gradient-to-br from-green-600 to-emerald-700 ring-4 ring-green-200 text-white'
                      : 'bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
                  }`}
                >
                  {loadingType === 'Warmup' ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      <span className="font-semibold text-sm sm:text-base">Creating...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 sm:gap-4">
                      <Timer className="h-8 w-8 sm:h-16 sm:w-16 text-white" strokeWidth={1.5} />
                        <div className="text-center">
                          <div className="font-bold text-lg sm:text-xl">Warmup</div>
                          <div className="text-xs sm:text-sm opacity-90">{getQuestionAccessDescription(3)}</div>
                        </div>
                      </div>
                    )}
                </button>

                {/* Study Session Button */}
                <button
                  onClick={() => selectInterviewType(7, 'Study Session')}
                  disabled={!canCreateInterviews || loadingType !== null || accessLoading || !!(getQuestionSetAccess(7) && !getQuestionSetAccess(7)?.allowed)}
                  className={`group relative rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                    selectedInterviewCount === 7
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 ring-4 ring-blue-200 text-white'
                      : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                  }`}
                >
                  {loadingType === 'Study Session' ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      <span className="font-semibold text-sm sm:text-base">Creating...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 sm:gap-4">
                      <BookOpenCheck className="h-8 w-8 sm:h-16 sm:w-16 text-white" strokeWidth={1.5} />
                      <div className="text-center">
                        <div className="font-bold text-lg sm:text-xl">Study Session</div>
                        <div className="text-xs sm:text-sm opacity-90">{getQuestionAccessDescription(7)}</div>
                      </div>
                    </div>
                  )}
                  {!isDemo && !isPremium && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200">
                        <Lock className="h-6 w-6 text-slate-700" />
                      </div>
                    </div>
                  )}
                </button>

                {/* Gauntlet Button */}
                <button
                  onClick={() => selectInterviewType(15, 'Gauntlet')}
                  disabled={!canCreateInterviews || loadingType !== null || accessLoading || !!(getQuestionSetAccess(15) && !getQuestionSetAccess(15)?.allowed)}
                  className={`group relative rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                    selectedInterviewCount === 15
                      ? 'bg-gradient-to-br from-red-600 to-rose-700 ring-4 ring-red-200 text-white'
                      : 'bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
                  }`}
                >
                  {loadingType === 'Gauntlet' ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                      <span className="font-semibold text-sm sm:text-base">Creating...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 sm:gap-4">
                      <Swords className="h-8 w-8 sm:h-16 sm:w-16 text-white" strokeWidth={1.5} />
                      <div className="text-center">
                        <div className="font-bold text-lg sm:text-xl">Gauntlet</div>
                        <div className="text-xs sm:text-sm opacity-90">{getQuestionAccessDescription(15)}</div>
                      </div>
                    </div>
                  )}
                  {!isDemo && !isPremium && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200">
                        <Lock className="h-6 w-6 text-slate-700" />
                      </div>
                    </div>
                  )}
                </button>
              </div>

              {selectedInterviewCount !== null && (
                <div className="rounded-2xl border border-blue-200 bg-white/90 p-4 sm:p-5 space-y-4 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-blue-900">
                        How do you want to practice?
                      </h3>
                    </div>
                  </div>

                  {isJobSelectorMode && (
                    <div className="space-y-2">
                      <Label htmlFor="job-select">Interview target</Label>
                      <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                        <SelectTrigger id="job-select" className="bg-white">
                          <SelectValue placeholder="Choose a job or go general" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={GENERAL_JOB_VALUE}>General interview</SelectItem>
                          {availableJobs.map((job) => (
                            <SelectItem key={job.id} value={job.id}>
                              {job.title} at {job.company}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs sm:text-sm text-slate-600">
                        {selectedJobId === GENERAL_JOB_VALUE
                          ? 'No job selected. We will create a general interview grounded in your resume and broader interview themes.'
                          : 'If this job already has an unfinished interview, we will let you know instead of creating another one.'}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <Label htmlFor="focus-area" className={hasLockedCustomization ? 'text-slate-500' : ''}>Should we focus on a particular topic?</Label>
                            {hasLockedCustomization && (
                              <button
                                type="button"
                                onClick={() => handleLockedClick('Premium interview customization')}
                                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                              >
                                <Lock className="h-3.5 w-3.5" />
                                Premium
                              </button>
                            )}
                          </div>
                          <p className={`text-xs sm:text-sm ${hasLockedCustomization ? 'text-slate-500' : 'text-slate-600'}`}>
                            Steer the interview toward a specific focus derived from the role, or specify your own.
                          </p>
                          <Select
                            value={selectedFocusArea}
                            onValueChange={setSelectedFocusArea}
                            disabled={hasLockedCustomization}
                          >
                            <SelectTrigger id="focus-area" className={hasLockedCustomization ? 'bg-slate-50 border-slate-200 text-slate-400' : 'bg-white'}>
                              <SelectValue placeholder="General - no focus" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No special focus</SelectItem>
                              {visibleFocusAreas.map((focus) => (
                                <SelectItem key={focus} value={focus}>{focus}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="focus-instructions" className={hasLockedCustomization ? 'text-slate-500' : ''}>Custom instructions</Label>
                          <Textarea
                            id="focus-instructions"
                            value={customFocusInstructions}
                            onChange={(e) => setCustomFocusInstructions(e.target.value)}
                            placeholder="Example: emphasize stakeholder tradeoffs and cross-team execution"
                            maxLength={512}
                            rows={5}
                            disabled={hasLockedCustomization}
                            className={`min-h-[4.75rem] resize-y text-base md:text-sm ${hasLockedCustomization ? 'bg-slate-50 border-slate-200 text-slate-400 placeholder:text-slate-400' : ''}`}
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between gap-3">
                            <Label className={hasLockedCustomization ? 'text-slate-500' : ''}>Add a question on...</Label>
                            {hasLockedCustomization && (
                              <button
                                type="button"
                                onClick={() => handleLockedClick('Premium interview customization')}
                                className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                              >
                                <Lock className="h-3.5 w-3.5" />
                                Premium
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          {GENERAL_CATEGORY_OPTIONS.map((option) => {
                            const active = generalCategories[option.key];
                            return (
                              <button
                                key={option.key}
                                type="button"
                                onClick={() => {
                                  if (hasLockedCustomization) {
                                    handleLockedClick('Premium interview customization');
                                    return;
                                  }
                                  toggleGeneralCategory(option.key);
                                }}
                                aria-disabled={hasLockedCustomization}
                                className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                                  hasLockedCustomization
                                    ? 'border-slate-200 bg-slate-50 text-slate-400'
                                    : active
                                      ? 'border-sky-300 bg-sky-50 text-slate-900 shadow-sm'
                                      : 'border-slate-200 bg-white text-slate-400 hover:border-sky-200 hover:text-slate-700'
                                }`}
                              >
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full border transition ${
                                  hasLockedCustomization
                                    ? 'border-slate-300 bg-white text-transparent'
                                    : active ? 'border-sky-400 bg-sky-100 text-sky-600' : 'border-slate-300 bg-white text-transparent'
                                }`}>
                                  <Check className="h-3.5 w-3.5" />
                                </span>
                                <span className="flex-1">
                                  <span className={`block text-sm font-medium ${hasLockedCustomization ? 'text-slate-500' : active ? 'text-slate-900' : 'text-slate-500'}`}>{option.label}</span>
                                  <span className={`block text-xs ${hasLockedCustomization ? 'text-slate-400' : active ? 'text-slate-600' : 'text-slate-400'}`}>{option.description}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                  {hasLockedCustomization && (
                    <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-blue-900">Premium interview customization</h3>
                        <p className="text-xs sm:text-sm text-blue-700 mt-1">
                          Preview the focus picker and question-style swaps here. Upgrade to turn them on for this interview.
                        </p>
                      </div>
                      <Button variant="outline" onClick={() => handleLockedClick('Premium interview customization')}>
                        <Lock className="w-4 h-4 mr-2" />
                        Unlock
                      </Button>
                    </div>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedInterviewCount(null)}
                      className="text-sm text-slate-600 hover:text-slate-900"
                    >
                      Cancel
                    </button>
                    <Button
                      onClick={() => {
                        if (selectedInterviewCount !== null) {
                          handleCreateInterview(selectedInterviewCount);
                        }
                      }}
                      disabled={!canCreateInterviews || loadingType !== null || accessLoading}
                      className="min-w-[140px]"
                    >
                      {loadingType === getInterviewTypeFromCount(selectedInterviewCount!) ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        'Go'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {listControls && (
            <div className="pt-1 sm:pt-2">
              {listControls}
            </div>
          )}

          {/* Interview Cards */}
          {sortedInterviews.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600 px-2">
                      {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages - 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:gap-3 xl:grid-cols-2">
                {paginatedInterviews.map((interview) => {
                  const status = interview.status.toUpperCase();
                  const isGenerating = status === 'CREATING' || status === 'RESEARCHING' || status === 'GENERATING';
                  const isFailed = status === 'FAILED';

                  if (isGenerating) {
                    // Special card for interviews being generated
                    return (
                      <Card
                        key={interview.id}
                        className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50"
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1">
                              <div className="relative">
                                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Loader2 className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 animate-spin" />
                                </div>
                                <div className="absolute inset-0 bg-blue-200 rounded-full animate-ping opacity-20"></div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm sm:text-lg font-semibold text-blue-900 mb-1">
                                  Preparing your interview
                                </div>
                                <div className="text-xs sm:text-sm text-blue-600">
                                  {status === 'CREATING' && 'Signaling the Coach...'}
                                  {status === 'RESEARCHING' && 'Researching your industry and experience (this can take a few minutes, so grab a drink and take a stretch break)...'}
                                  {status === 'GENERATING' && 'Generating personalized questions (only a minute or two more)...'}
                                </div>
                                {(status === 'RESEARCHING' || status === 'GENERATING') && (
                                  <div className="mt-2 sm:mt-3">
                                    <div className="inline-block max-w-full rounded-md border border-blue-100 bg-white/70 px-3 py-1.5">
                                      <div className="text-xs sm:text-sm text-slate-600 leading-normal">
                                        <span className="font-semibold text-slate-700">Fun Definitely Not Fake Fact:</span> {FUN_FACTS[funFactIndex]}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right text-xs sm:text-sm text-gray-500 flex-shrink-0">
                              <div className="hidden sm:block">Created</div>
                              <div className="font-medium">
                                {new Date(interview.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }

                  if (isFailed) {
                    return (
                      <Card
                        key={interview.id}
                        className="border-red-200 bg-gradient-to-r from-red-50 to-rose-50"
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 sm:h-12 sm:w-12">
                              <Timer className="h-4 w-4 text-red-700 sm:h-6 sm:w-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-red-950 sm:text-lg">
                                I&apos;m sorry, something went wrong
                              </div>
                              <div className="mt-1 text-xs text-red-800 sm:text-sm">
                                We hit an issue while generating this interview, so it could not be completed.
                              </div>
                              <div className="mt-1 text-xs text-red-700 sm:text-sm">
                                We&apos;ve already contacted the dev team to investigate. Please create a new interview to try again.
                              </div>
                            </div>
                            <div className="text-right text-xs text-red-700 sm:text-sm">
                              <div className="hidden sm:block">Created</div>
                              <div className="font-medium">
                                {new Date(interview.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }

                  // Regular card for completed interviews
                  const type = getInterviewType(interview);
                  const typeColor = getInterviewTypeColor(type);
                  const isComplete = status === 'COMPLETE';
                  const isIncomplete = status === 'INCOMPLETE';

                  return (
                    <Card
                      key={interview.id}
                      onClick={() => handleInterviewClick(interview.id)}
                      className={`cursor-pointer hover:shadow-lg transition-all duration-200 border-2 ${
                        isComplete && interview.overall_score !== undefined
                          ? `${getGradeCardBackground(interview.overall_score)} hover:scale-102`
                          : isIncomplete
                          ? 'border-green-200 hover:border-green-300 bg-gradient-to-br from-green-50 to-emerald-50'
                          : 'border-gray-200 hover:border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50'
                      }`}
                    >
                      <CardContent className="p-3 sm:p-3.5">
                        {isComplete && interview.overall_score !== undefined ? (
                          // Grade card layout - perfectly centered grade with side elements
                          <div className="space-y-3">
                            {isJobSelectorMode && (
                              <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                                {getInterviewJobLabel(interview)}
                              </div>
                            )}
                            <div className="relative flex items-center justify-between min-h-[56px] sm:min-h-[72px]">
                              {/* Left - Interview Type Badge */}
                              <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                                <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${typeColor}`}>
                                  {type}
                                </div>
                              </div>

                              {/* Absolutely Centered Grade Letter */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className={`text-3xl sm:text-4xl font-black ${getGradeTextColor(interview.overall_score)} drop-shadow-lg`}>
                                  {getScoreLabel(interview.overall_score)}
                                </div>
                              </div>

                              {/* Right - Created Date */}
                              <div className="text-right text-xs sm:text-sm flex-shrink-0">
                                <div className="text-xs opacity-75 hidden sm:block">Created</div>
                                <div className="font-medium">
                                  {new Date(interview.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Regular card layout for non-graded interviews
                          <div className="space-y-3">
                            {isJobSelectorMode && (
                              <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                                {getInterviewJobLabel(interview)}
                              </div>
                            )}
                            <div className="relative flex items-center justify-between min-h-[56px] sm:min-h-[72px]">
                              {/* Left - Interview Type Badge */}
                              <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                                <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${typeColor}`}>
                                  {type}
                                </div>
                              </div>

                              {/* Absolutely Centered Content */}
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                {isIncomplete ? (
                                  // Large centered Start button for incomplete interviews
                                  <div className="flex items-center gap-2 sm:gap-3 pointer-events-auto cursor-pointer group">
                                    <div className="flex items-center gap-2 sm:gap-3 text-green-600 hover:text-green-700 transition-colors">
                                      <div className="p-1 sm:p-2 rounded-full group-hover:bg-green-50 transition-all">
                                        <Play className="h-6 w-6 sm:h-10 sm:w-10" fill="currentColor" />
                                      </div>
                                      <span className="text-lg sm:text-2xl font-black tracking-wide">START</span>
                                    </div>
                                  </div>
                                ) : (
                                  // Show status pill for other statuses
                                  <div className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-sm sm:text-lg font-bold ${getStatusPillStyle(interview.status)} hover:bg-gray-50 transition-colors`}>
                                    {formatStatusText(interview.status)}
                                  </div>
                                )}
                              </div>

                              {/* Right - Created Date */}
                              <div className="text-right text-xs sm:text-sm text-gray-600 flex-shrink-0">
                                <div className="text-xs text-gray-500 hidden sm:block">Created</div>
                                <div className="font-medium">
                                  {new Date(interview.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
          {/* No interviews message */}
          {interviews.length === 0 && (
            <div className="text-center py-4">
              <div className="text-blue-700 mb-2">
                {emptyMessage}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      {/* Premium lock modal */}
      <PremiumLockModal
        open={showPremiumModal}
        onOpenChange={(open) => {
          if (!open) setPremiumFeatureLabel(undefined);
          setShowPremiumModal(open);
        }}
        featureLabel={premiumFeatureLabel ?? 'Practice Interviews'}
        description={
          premiumFeatureLabel
            ? `${premiumFeatureLabel} interviews are available for Premium members. Upgrade to unlock unlimited practice and advanced sessions.`
            : loadingType
            ? `${loadingType} interviews are available for Premium members. Upgrade to unlock unlimited practice and advanced sessions.`
            : undefined
        }
        ctaLabel="Subscribe now"
      />
    </Card>
  );
}
